import type { DialogBarAction } from '../Types';
import type { AppBarProps } from '@mui/material/AppBar';
import type { SvgIconProps } from '@mui/material/SvgIcon';
import type { ToolbarProps } from '@mui/material/Toolbar';

import CloseIcon from '@mui/icons-material/Close';
import AppBar from '@mui/material/AppBar';
import IconButton from '@mui/material/IconButton';
import { styled } from '@mui/material/styles';
import * as React from 'react';
import { useTranslation } from 'react-i18next';

import { DialogContext } from '../context/DialogContext';

import { DialogBarActions } from './DialogBarActions';

import { ToolbarLabel } from '@egr/xbox/egr-gui-elements/Toolbar/ToolbarLabel';
import { TopToolbar } from '@egr/xbox/egr-gui-elements/Toolbar/TopToolbar';

export interface DialogBarProps extends AppBarProps {
    label?: string | React.ReactNode;
    close?: () => void;
    onBarClick?: () => void;
    closeIcon?: React.ComponentType<SvgIconProps>;

    actions?: Array<DialogBarAction>;
    toolbarProps?: ToolbarProps;

    bottomDivider?: boolean;
}

const StyledAppBar: React.FC<AppBarProps> = styled(AppBar)(({ theme }) => ({
    background: theme.palette.background.default,
    color: theme.palette.text.primary,
    borderTopRightRadius: 4,
    borderTopLeftRadius: 4,
}));

export const DialogBar: React.FC<DialogBarProps> = ({ label, close, onBarClick, closeIcon: CustomCloseIcon = CloseIcon, actions, toolbarProps, bottomDivider, position = 'static', ...appBarProps }) => {
    const { fullscreen } = React.useContext(DialogContext);
    const { t } = useTranslation();

    function closeButton(): React.ReactNode {
        if (close == null) {
            return null;
        }

        return (
            <IconButton
                data-testid="dialogbar-closeIcon"
                onClick={close}
                edge="start"
                size="large"
                aria-label={t('Close')}
            >
                <CustomCloseIcon data-testid="dialogbar-closeIcon"/>
            </IconButton>
        );
    }

    function onClick(): void {
        if (onBarClick != null) {
            onBarClick();
        }
    }

    return (
        <StyledAppBar
            {...appBarProps}
            position={position}
            elevation={0}
            sx={bottomDivider ? {borderBottom: '1px solid', borderBottomColor: 'divider'} : undefined}
        >
            <TopToolbar noMargin={!fullscreen} {...toolbarProps}>
                {closeButton()}
                <ToolbarLabel onClick={onClick ?? undefined}>{label}</ToolbarLabel>
                <DialogBarActions actions={actions}/>
            </TopToolbar>
        </StyledAppBar>
    );
};